import { AppLayout } from '@/components/AppLayout';
import { ForkHirePerformanceCharts } from '@/components/analytics/ForkHirePerformanceCharts';
import { ServicePageGuard } from '@/components/ServicePageGuard';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { useForkHireAnalytics } from '@/hooks/useForkHireAnalytics';
import { AlertCircle, CheckCircle2, ClipboardList, Star, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type MetricTone = 'primary' | 'warning' | 'violet' | 'success' | 'destructive';

const metricToneClasses: Record<MetricTone, { icon: string; text: string; bg: string }> = {
  primary: {
    icon: 'text-primary',
    text: 'text-primary',
    bg: 'bg-primary/10',
  },
  warning: {
    icon: 'text-warning',
    text: 'text-warning',
    bg: 'bg-warning/10',
  },
  violet: {
    icon: 'text-brand-violet',
    text: 'text-brand-violet',
    bg: 'bg-brand-violet/10',
  },
  success: {
    icon: 'text-success',
    text: 'text-success',
    bg: 'bg-success/10',
  },
  destructive: {
    icon: 'text-destructive',
    text: 'text-destructive',
    bg: 'bg-destructive/10',
  },
};

export default function Analytics() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useForkHireAnalytics();

  const analytics = data ?? {
    summary: {
      totalApplications: 0,
      scored: 0,
      interviewed: 0,
      hired: 0,
      rejected: 0,
    },
    perJob: [],
  };
  const { summary } = analytics;

  const summaryMetrics = [
    { key: 'totalApplications', label: t('hire.analytics.totalApplications'), count: summary.totalApplications, tone: 'primary' as const, Icon: ClipboardList },
    { key: 'scored', label: t('hire.analytics.scored'), count: summary.scored, tone: 'warning' as const, Icon: Star },
    { key: 'interviewed', label: t('hire.analytics.interviewed'), count: summary.interviewed, tone: 'violet' as const, Icon: UserCheck },
    { key: 'hired', label: t('hire.analytics.hired'), count: summary.hired, tone: 'success' as const, Icon: CheckCircle2 },
    { key: 'rejected', label: t('hire.analytics.rejected'), count: summary.rejected, tone: 'destructive' as const, Icon: UserX },
  ];

  return (
    <AppLayout>
      <ServicePageGuard serviceKey={SERVICE_KEYS.ANALYTICS}>
        <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
          <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
            <div className="p-5 sm:p-6">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{t('hire.analytics.title')}</h1>
              <p className="text-muted-foreground mt-1.5 text-sm">{t('hire.analytics.subtitle')}</p>
            </div>
          </Card>

          {isLoading ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((item) => <Skeleton key={item} className="h-28 w-full rounded-lg" />)}
              </div>
              <Skeleton className="h-64 w-full rounded-lg" />
              <Skeleton className="h-72 w-full rounded-lg" />
            </div>
          ) : isError ? (
            <Card className="p-6 shadow-card border-border rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display font-semibold text-foreground text-base">{t('hire.analytics.loadErrorTitle')}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{t('hire.analytics.loadErrorDescription')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  {t('hire.analytics.retry')}
                </button>
              </div>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                {summaryMetrics.map(({ key, label, count, tone, Icon }) => {
                  const toneClasses = metricToneClasses[tone];
                  return (
                    <Card key={key} className="p-4 shadow-card border-border rounded-lg">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-muted-foreground">{label}</p>
                          <p className={`mt-2 text-2xl font-bold tabular-nums ${toneClasses.text}`} dir="ltr">{count}</p>
                        </div>
                        <div className={`w-10 h-10 rounded-lg ${toneClasses.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`w-5 h-5 ${toneClasses.icon}`} />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <ForkHirePerformanceCharts summary={summary} />
            </>
          )}
        </div>
      </ServicePageGuard>
    </AppLayout>
  );
}
